import { setMapProductsStepTestId } from "@bcc/components/onboarding/map-products-step.utils";
import { setOnboardingStepperTestId } from "@bcc/components/onboarding/onboarding-stepper.utils";
import {
  REMOTE_VARIANT_MULTI_SELECT_PREFIX,
  setAccordionTriggerTestId,
  setProductSyncFooterCountTestId,
  setProductSyncModalTestId,
} from "@bcc/components/products/product-sync-modal/product-sync-modal.utils";
import { setMultiSelectTriggerTestId } from "@bcc/components/ui/multi-select";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { setProductSyncRowTourId, TOUR_IDS } from "../../tour-ids";
import type { FeatureTour } from "../../tour.types";
import { testIdContainsSelector, testIdPrefixSelector, testIdSelector, tourTargetSelector } from "../../tour.utils";
import { ONBOARDING_ROUTE_PATTERNS } from "./connect.tour";

const onPage = { route: ROUTE_PATHS.ONBOARDING_STEP_2, alternateRoutePatterns: ONBOARDING_ROUTE_PATTERNS };

/** Absent when step 2 shows the SKU-import variant instead of product mapping */
const PAGE = testIdSelector(setMapProductsStepTestId("container"));
const UNMAPPED_ROW = tourTargetSelector(setProductSyncRowTourId("unmapped"));
/** Bag-size trigger ids start with the variant id, so match on the fixed middle part */
const UNMAPPED_ROW_TRIGGER = `${UNMAPPED_ROW} button${testIdContainsSelector(setAccordionTriggerTestId("", ""))}`;
/** Mounts only while its row is expanded */
const STORE_PRODUCT_SELECT = `${UNMAPPED_ROW} ${testIdPrefixSelector(setMultiSelectTriggerTestId(REMOTE_VARIANT_MULTI_SELECT_PREFIX))}`;

/** Mapping guide on /onboarding/step-2: explains the page and opens the first bag size still to map */
export const setupMapTour: FeatureTour = {
  id: "setupMap",
  titleKey: "tour.features.setupMap.title",
  order: 2,
  steps: [
    {
      id: "setupMap.stepper",
      ...onPage,
      target: testIdSelector(setOnboardingStepperTestId("container")),
      extendTargets: [testIdSelector(setMapProductsStepTestId("heading"))],
      titleKey: "tour.steps.setupMap.stepper.title",
      bodyKey: "tour.steps.setupMap.stepper.body",
      placement: "bottom",
      skipUnless: PAGE,
    },
    {
      id: "setupMap.actions",
      ...onPage,
      target: testIdSelector(setMapProductsStepTestId("skip-button")),
      extendTargets: [testIdSelector(setMapProductsStepTestId("back-button"))],
      titleKey: "tour.steps.setupMap.actions.title",
      bodyKey: "tour.steps.setupMap.actions.body",
      placement: "bottom",
      skipUnless: PAGE,
    },
    {
      id: "setupMap.products",
      ...onPage,
      target: tourTargetSelector(TOUR_IDS.products.syncList),
      extendTargets: [testIdSelector(setProductSyncModalTestId("search-input"))],
      titleKey: "tour.steps.setupMap.products.title",
      bodyKey: "tour.steps.setupMap.products.body",
      placement: "left",
      skipUnless: PAGE,
      emptyState: { detectTarget: UNMAPPED_ROW, bodyKey: "tour.steps.setupMap.products.emptyBody" },
    },
    {
      id: "setupMap.store-product",
      ...onPage,
      target: STORE_PRODUCT_SELECT,
      // Expands the first unmapped row; `closed` keeps the retried click from collapsing it again
      prepare: [{ clickTarget: `${UNMAPPED_ROW_TRIGGER}[data-state="closed"]` }],
      titleKey: "tour.steps.setupMap.storeProduct.title",
      bodyKey: "tour.steps.setupMap.storeProduct.body",
      placement: "left",
      skipUnless: UNMAPPED_ROW,
      targetTimeoutMs: 4000,
    },
    {
      id: "setupMap.save",
      ...onPage,
      target: testIdSelector(setProductSyncModalTestId("continue-button")),
      extendTargets: [testIdSelector(setProductSyncFooterCountTestId("required-products"))],
      titleKey: "tour.steps.setupMap.save.title",
      bodyKey: "tour.steps.setupMap.save.body",
      placement: "top",
      skipUnless: PAGE,
      emptyState: { detectTarget: UNMAPPED_ROW, bodyKey: "tour.steps.setupMap.save.emptyBody" },
    },
  ],
};
